import { createContext, useContext } from "react";

import { createStore, useStore } from "zustand";

import { CellMappingInfo, TagInfo } from "@/apis/table/types";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";

import { Dimension, Role } from "@/types/mapping";
import { CellsRange } from "@/types/spreadsheet";

export type TagView = {
  key: string;
  role: string;
};

export enum CellType {
  HEADER = "HEADER",
  DATA = "DATA",
  SUM = "SUM",
  SECTION = "SECTION",
  NONE = "NONE",
}
export type Fact = CellsRange & {
  table: {
    id: string;
  };
  roleKey: string;
  cellType: CellType;
};

const areTagParentsEqual = (
  left: TagInfo["parent"],
  right: TagInfo["parent"],
) => {
  if (left === right) return true;
  if (!left || !right) return false;

  return (
    left.id === right.id &&
    left.name === right.name &&
    left.label === right.label &&
    left.prefix === right.prefix &&
    left.type === right.type
  );
};

const areTagsEqual = (left: TagInfo, right: TagInfo) => {
  return (
    left.id === right.id &&
    left.name === right.name &&
    left.prefix === right.prefix &&
    left.type === right.type &&
    left.label === right.label &&
    left.roleKey === right.roleKey &&
    areTagParentsEqual(left.parent, right.parent)
  );
};

const areTagListsEqual = (left: TagInfo[], right: TagInfo[]) => {
  // Order is significant: later tags win in single-select cleanup.
  return (
    left.length === right.length &&
    left.every((tag, index) => areTagsEqual(tag, right[index]))
  );
};

const areMappingInfosEqual = (
  left: CellMappingInfo | undefined,
  right: CellMappingInfo,
) => {
  return (
    areTagListsEqual(left?.tags ?? [], right.tags ?? []) &&
    (left?.accountingPeriod ?? null) === (right.accountingPeriod ?? null) &&
    (left?.targetYear ?? null) === (right.targetYear ?? null) &&
    (left?.accumulated ?? null) === (right.accumulated ?? null)
  );
};

export type MappingViewState =
  | "DEFAULT"
  | "ROLE_SELECTION"
  | "SHEET_CHANGED"
  | "EXTENDING";

type MappingState = {
  viewState: {
    current: MappingViewState;
    set: (state: MappingViewState) => void;
    reset: () => void;
  };
  fact: {
    selected: Fact | null;
    set: (cell: Fact | null) => void;
  };
  mappedInfos: {
    updatedAt: Date;
    setUpdatedAt: () => void;
    selected: {
      [key: string]: CellMappingInfo;
    };
    add: (params: {
      address: string;
      tagInput: TagInfo | TagInfo[];
      period: AccountingPeriod | null;
      targetYear: TargetYear | null;
      accumulated: Accumulated | null;
    }) => void;
    set: (params: { address: string; mappingInfo: CellMappingInfo }) => void;
    remove: (address: string, tag: TagInfo | TagInfo[]) => void;
    clearSheet: (sheetId: string) => void;
    mutation: {
      mapping: {
        isMapping: boolean;
        isMappingSuccess: boolean;
        set: (isMapping: boolean) => void;
        setIsMappingSuccess: (isMappingSuccess: boolean) => void;
      };
    };
  };
  detail: {
    viewTag: TagView | null;
    inRole: boolean;
    currentRole: Role | null;
    tagLabel: string | null;
    setInRole: (inRole: boolean) => void;
    setTagLabel: (tagLabel: string) => void;
    setCurrentRole: (role: Role) => void;
    set: (tag: TagView | null) => void;
    clear: () => void;
  };
  extend: {
    koLabel: string | null;
    showSimilar: boolean;
    type: "MEMBER" | "LINE_ITEM" | "LINE_ITEMS" | null;
    address: string | null;
    parent: {
      key: string | null;
      label: string | null;
      role: string;
    } | null;
    role: {
      key: string | null;
    } | null;
    extendingChild: boolean;
    childInfo: {
      tagKey: string;
      koLabel: string;
      enLabel: string;
    } | null;
    setShowSimilar: (showSimilar: boolean) => void;
    setKoLabel: (koLabel: string) => void;
    setExtendingChild: (extendingChild: boolean) => void;
    setChildInfo: (
      childInfo: {
        tagKey: string;
        koLabel: string;
        enLabel: string;
      } | null,
    ) => void;
    setParent: (
      parent: { key: string; role: string; label: string } | null,
    ) => void;
    setRole: (key: string) => void;
    clearExtend: () => void;
  };
  search: {
    query: string | null;
    parent: string | null;
    setParent: (parent: string) => void;
    setQuery: (query: string) => void;
    clear: () => void;
  };

  dimension: {
    dimension: Dimension;
    previewDimension: Dimension;
    set: (dimension: Dimension) => void;
    setPreviewDimension: (dimension: Dimension) => void;
  };
  tablePreview: {
    targetYear: TargetYear;
    dimension: Dimension;
    selectedLineItem: {
      lineItemsId: string | null;
      lineItemId: string | null;
      depth: number | null;
    };
    selectedAxis: {
      axisId: string | null;
      memberId: string | null;
      depth: number | null;
    };
    setTargetYear: (targetYear: TargetYear) => void;
    setDimension: (dimension: Dimension) => void;
    setSelectedLineItem: (
      lineItemsId: string,
      lineItemId: string,
      depth: number,
    ) => void;
    setSelectedAxis: (axisId: string, memberId: string, depth: number) => void;
  };

  clear: () => void;
};

export const createMappingStore = () =>
  createStore<MappingState>()((set) => ({
    viewState: {
      current: "DEFAULT",
      set: (newState) => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            current: newState,
          },
        }));
      },
      reset: () => {
        set((state) => ({
          viewState: {
            ...state.viewState,
            current: "DEFAULT",
          },
        }));
      },
    },
    fact: {
      selected: null,
      set: (cell) => {
        // 현재 상태와 비교하여 실제로 변경이 있을 때만 업데이트
        set((state) => {
          const newDimension = { ...state.dimension };

          return {
            fact: {
              ...state.fact,
              selected: cell,
            },
            dimension: newDimension,
          };
        });
      },
    },
    mappedInfos: {
      updatedAt: new Date(),
      setUpdatedAt: () => {
        set((state) => ({
          mappedInfos: {
            ...state.mappedInfos,
            updatedAt: new Date(),
          },
        }));
      },
      selected: {},
      add: (params) => {
        const { address, tagInput, period, targetYear, accumulated } = params;
        set((state) => {
          const mappedInfos = state.mappedInfos.selected[address];
          const existingTags = mappedInfos?.tags ?? [];
          const tagsToAdd = Array.isArray(tagInput) ? tagInput : [tagInput];
          const processedTags = [...existingTags];

          for (const tag of tagsToAdd) {
            // 이미 존재하는 태그 제거
            const filteredTags = processedTags.filter(
              (t) => t.name !== tag.name,
            );
            // Add 새로운 태그
            filteredTags.push(tag);

            processedTags.length = 0;
            processedTags.push(...filteredTags);
          }

          const nextMappingInfo = {
            tags: processedTags,
            accountingPeriod: period,
            targetYear: targetYear,
            accumulated: accumulated,
          };

          if (areMappingInfosEqual(mappedInfos, nextMappingInfo)) {
            return state;
          }

          return {
            mappedInfos: {
              ...state.mappedInfos,
              selected: {
                ...state.mappedInfos.selected,
                [address]: nextMappingInfo,
              },
            },
          };
        });
      },
      set: (params) => {
        const { address, mappingInfo } = params;
        set((state) => {
          return {
            mappedInfos: {
              ...state.mappedInfos,
              selected: {
                ...state.mappedInfos.selected,
                [address]: {
                  ...mappingInfo,
                },
              },
            },
          };
        });
      },
      clearSheet: (sheetId) => {
        set((state) => {
          const newSelected = Object.fromEntries(
            Object.entries(state.mappedInfos.selected).filter(
              ([key]) => !key.includes(sheetId),
            ),
          );
          return {
            mappedInfos: {
              ...state.mappedInfos,
              selected: newSelected,
            },
          };
        });
      },
      remove: (address, tagInput) => {
        set((state) => {
          const mappedInfos = state.mappedInfos.selected[address] || {
            tags: [],
          };
          const existingTags = mappedInfos.tags ?? [];
          const tagsToRemove = Array.isArray(tagInput) ? tagInput : [tagInput];
          const tagNamesToRemove = new Set(tagsToRemove.map((tag) => tag.name));
          const nextTags = existingTags.filter(
            (tag) => !tagNamesToRemove.has(tag.name),
          );

          if (nextTags.length === existingTags.length) {
            return state;
          }

          return {
            mappedInfos: {
              ...state.mappedInfos,
              selected: {
                ...state.mappedInfos.selected,
                [address]: {
                  ...mappedInfos,
                  tags: nextTags,
                },
              },
            },
          };
        });
      },
      mutation: {
        mapping: {
          isMapping: false,
          isMappingSuccess: false,
          set: (isMapping) => {
            set((state) => ({
              mappedInfos: {
                ...state.mappedInfos,
                mutation: {
                  ...state.mappedInfos.mutation,
                  mapping: {
                    ...state.mappedInfos.mutation.mapping,
                    isMapping,
                  },
                },
              },
            }));
          },
          setIsMappingSuccess: (isMappingSuccess) => {
            set((state) => ({
              mappedInfos: {
                ...state.mappedInfos,
                mutation: {
                  ...state.mappedInfos.mutation,
                  mapping: {
                    ...state.mappedInfos.mutation.mapping,
                    isMappingSuccess,
                  },
                },
              },
            }));
          },
        },
      },
    },
    detail: {
      viewTag: null,
      inRole: true,
      currentRole: null,
      tagLabel: null,
      setInRole: (inRole) => {
        set((state) => ({
          detail: { ...state.detail, inRole },
        }));
      },
      setTagLabel: (tagLabel) => {
        set((state) => ({
          detail: { ...state.detail, tagLabel },
        }));
      },
      setCurrentRole: (role: Role) => {
        set((state) => ({
          detail: { ...state.detail, currentRole: role },
        }));
      },
      set: (tag) => {
        set((state) => ({
          detail: { ...state.detail, viewTag: tag },
        }));
      },
      clear: () => {
        set((state) => ({
          detail: {
            ...state.detail,
            viewTag: null,
            inRole: true,
            currentRole: null,
            tagLabel: null,
          },
        }));
      },
    },
    search: {
      query: null,
      parent: null,
      setQuery: (query) => {
        set((state) => ({ search: { ...state.search, query } }));
      },
      setParent: (parent) => {
        set((state) => ({ search: { ...state.search, parent } }));
      },
      clear: () => {
        set((state) => ({
          search: {
            ...state.search,
            query: null,
            parent: null,
          },
        }));
      },
    },
    dimension: {
      dimension: "LINE_ITEM",
      previewDimension: "LINE_ITEM",
      set: (dimension) => {
        set((state) => ({
          dimension: { ...state.dimension, dimension },
        }));
      },
      setPreviewDimension: (dimension) => {
        set((state) => ({
          dimension: { ...state.dimension, previewDimension: dimension },
        }));
      },
    },
    extend: {
      type: null,
      koLabel: null,
      showSimilar: false,
      parent: null,
      role: {
        key: null,
      },
      address: null,
      childInfo: null,
      extendingChild: false,
      setKoLabel: (koLabel) => {
        set((state) => ({
          extend: {
            ...state.extend,
            koLabel,
          },
        }));
      },
      setShowSimilar: (showSimilar: boolean) => {
        set((state) => ({ extend: { ...state.extend, showSimilar } }));
      },
      setExtendingChild: (extendingChild) => {
        set((state) => ({ extend: { ...state.extend, extendingChild } }));
      },
      setChildInfo: (childInfo) => {
        set((state) => ({ extend: { ...state.extend, childInfo } }));
      },
      setRole: (key) => {
        set((state) => ({
          extend: { ...state.extend, role: { ...state.extend.role, key } },
        }));
      },
      setParent: (parent) => {
        set((state) => ({ extend: { ...state.extend, parent } }));
      },
      clearExtend: () => {
        set((state) => ({
          extend: {
            ...state.extend,
            koLabel: null,
            showSimilar: false,
            type: null,
            address: null,
            parent: null,
            role: null,
            extendingChild: false,
            childInfo: null,
          },
        }));
      },
    },
    tablePreview: {
      targetYear: TargetYear.CURRENT,
      dimension: "LINE_ITEM",
      selectedLineItem: {
        lineItemsId: null,
        lineItemId: null,
        depth: null,
      },
      selectedAxis: {
        axisId: null,
        memberId: null,
        depth: null,
      },
      setDimension: (dimension) => {
        set((state) => ({
          tablePreview: { ...state.tablePreview, dimension },
        }));
      },
      setTargetYear: (targetYear) => {
        set((state) => ({
          tablePreview: { ...state.tablePreview, targetYear },
        }));
      },
      setSelectedLineItem: (lineItemsId, lineItemId, depth) => {
        set((state) => ({
          tablePreview: {
            ...state.tablePreview,
            selectedLineItem: {
              lineItemsId: lineItemsId,
              lineItemId: lineItemId,
              depth,
            },
          },
        }));
      },
      setSelectedAxis: (axisId, memberId, depth) => {
        set((state) => ({
          tablePreview: {
            ...state.tablePreview,
            selectedAxis: { axisId, memberId, depth },
          },
        }));
      },
    },
    clear: () => {
      set((state) => {
        return {
          fact: { ...state.fact, selected: null },
          mappedInfos: { ...state.mappedInfos, selected: {} },
          detail: { ...state.detail, viewTag: null },
          search: {
            ...state.search,
            query: null,
            parent: null,
          },
          dimension: {
            ...state.dimension,
            dimension: "LINE_ITEM",
            previewDimension: "LINE_ITEM",
          },
          tablePreview: {
            ...state.tablePreview,
            targetYear: TargetYear.CURRENT,
            dimension: "LINE_ITEM",
            selectedLineItem: {
              lineItemsId: null,
              lineItemId: null,
              depth: null,
            },
            selectedAxis: { axisId: null, memberId: null, depth: null },
          },
          extend: {
            ...state.extend,
            type: null,
            koLabel: null,
            showSimilar: false,
            address: null,
            parent: null,
            role: { key: null },
            childInfo: null,
            extendingChild: false,
          },
        };
      });
    },
  }));

export const MappingStoreContext = createContext<ReturnType<
  typeof createMappingStore
> | null>(null);

export const useMappingStore = <T>(selector: (state: MappingState) => T): T => {
  const store = useContext(MappingStoreContext);

  if (!store) {
    throw new Error("Missing MappingStoreProvider in the component tree");
  }

  return useStore(store, selector);
};
